import { useEffect, useState } from 'react'
import { CompanyTag } from '@/components/ui/badge'
import { ViewTabs } from '@/components/ui/tabs'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { Loading } from 'components/Loading'
import _get from 'lodash/get'
import { CircleMinus, CirclePlus } from 'lucide-react'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import { SelectClientModal } from '../SelectClient.modal'
import { CreateAdjustmentTabs } from './Adjustment/CreateAdjustmentTabs'
import BonusPage from './Bonus/BonusPage'
import DepositPage from './Deposit/DepositPage'
import MultiAdjustmentPage from './Multi-Adjustment/MultiAdjustmentPage'
import PaymentAgentDepositPage from './PaymentAgentDeposit/PaymentAgentDepositPage'
import PaymentAgentWithdrawalPage from './PaymentAgentWithdrawal/PaymentAgentWithdrawalPage'
import TradingAccountToWalletTransferPage from './TradingAccountToWalletTransfer/TradingAccountToWalletTransferPage'
import { TransactionNegativeEquityPage } from './TransactionNegativeEquity/TransactionNegativeEquityPage'
import TransferPage from './Transfers/TransferPage'
import WalletToToTradingAccountTransferPage from './WalletToTradingAccountTransfer/WalletToTradingAccountTransferPage'
import WithdrawalPage from './Withdrawal/WithdrawalPage'

import './transaction.scss'

import { useLocation } from 'react-router-dom'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

export enum TransactionTabType {
  TRANSFERS = 'TRANSFERS',
  FROM_WALLET_TO_TRADING_ACCOUNT_TRANSACTION = 'FROM_WALLET_TO_TRADING_ACCOUNT_TRANSACTION',
  FROM_TRADING_ACCOUNT_TO_WALLET_TRANSACTION = 'FROM_TRADING_ACCOUNT_TO_WALLET_TRANSACTION',
  ADJUSTMENTS = 'ADJUSTMENTS',
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
  MULTI_ADJUSTMENTS = 'MULTI_ADJUSTMENTS',
  BONUS = 'BONUS',
  PA_DEPOSIT = 'PA_DEPOSIT',
  PA_WITHDRAW = 'PA_WITHDRAW',
  NEGATIVE_EQUITY = 'NEGATIVE_EQUITY',
  CONVERSION = 'CONVERSION',
}

interface LocationState {
  client?: ClientDto | { id: string; name: string }
  wallet?: WalletDto | { id: string; name: string }
  tradingAccount?: TradingAccountDto
  tab?: TransactionTabType
}
const TransactionsTab = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [isSelectClientModalOpen, setSelectClientModalOpen] = useState<boolean>(false)
  const [isLoading, setLoading] = useState(false)
  const [client, setClient] = useState<ClientDto | null>(null)
  const [wallet, setWallet] = useState<WalletDto | undefined>(undefined)
  const [tradingAccount, setTradingAccount] = useState<TradingAccountDto | undefined>(undefined)
  const location = useLocation()

  const getClientById = async (clientId: string) => {
    setLoading(true)
    setClient(await clientsApiClient.getClientBasicDto(clientId))
    setLoading(false)
  }

  const getWalletById = async (walletId: string) => {
    setLoading(true)
    setWallet(await walletsApiClient.getWalletById(walletId))
    setLoading(false)
  }

  useEffect(() => {
    const predefinedClient = (location.state as LocationState)?.client
    const predefinedWallet = (location.state as LocationState)?.wallet
    const predefinedTradingAccount = (location.state as LocationState)?.tradingAccount

    if (predefinedClient) {
      if ('username' in predefinedClient) {
        setClient(predefinedClient)
      } else {
        getClientById(predefinedClient.id)
      }
    }
    if (predefinedWallet) {
      if ('client' in predefinedWallet) {
        getClientById(predefinedWallet.client.id)
        setWallet(predefinedWallet)
      } else {
        getWalletById(predefinedWallet.id)
      }
    }
    if (predefinedTradingAccount) {
      setTradingAccount(predefinedTradingAccount)
    }
  }, [location.state]) // eslint-disable-line react-hooks/exhaustive-deps

  const getSelectedClient = (selectedClient: ClientDto) => {
    setClient(selectedClient)
    setSelectClientModalOpen(false)
  }

  const tab: TransactionTabType = _get(location, 'state.tab', TransactionTabType.DEPOSIT)

  return (
    <Loading isLoading={isLoading}>
      <SelectClientModal
        open={isSelectClientModalOpen}
        onOpenChange={setSelectClientModalOpen}
        getClient={getSelectedClient}
      />

      <header className='transaction-client__header'>
        <div className='transaction-client__info'>
          {client && (
            <>
              <span>{client.name}</span>
              <CompanyTag id={client.tickmillCompany.id}>{client.tickmillCompany.name}</CompanyTag>
            </>
          )}
        </div>
      </header>
      <ViewTabs
        defaultKey={tab}
        tabs={[
          {
            key: TransactionTabType.DEPOSIT,
            translateKey: 'Deposit',
            visible: true,
            permission: PermissionsEnum.CanAddWalletDepositTransaction,
            component: (
              <DepositPage
                client={client}
                preSelectedWallet={wallet}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.WITHDRAWAL,
            translateKey: 'Withdrawal',
            visible: true,
            permission: PermissionsEnum.CanAddWalletWithdrawalTransaction,
            component: (
              <WithdrawalPage
                client={client}
                preSelectedWallet={wallet}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.TRANSFERS,
            translateKey: 'Transfers',
            visible: true,
            permission: PermissionsEnum.CanAddCrossWalletTransaction,
            component: (
              <TransferPage
                client={client}
                preSelectedWallet={wallet}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.FROM_WALLET_TO_TRADING_ACCOUNT_TRANSACTION,
            translateKey: (
              <span>
                Trading account
                <Tooltip key='right'>
                  <TooltipTrigger>
                    <CirclePlus className='ml-3 size-4 text-green-600' />
                  </TooltipTrigger>
                  <TooltipContent side='right'>
                    Transfer From Wallet to Trading Account
                  </TooltipContent>
                </Tooltip>
              </span>
            ),
            visible: true,
            permission: PermissionsEnum.CanTransferFromWalletToTradingAccountTransaction,
            component: (
              <WalletToToTradingAccountTransferPage
                client={client}
                preSelectedWallet={wallet}
                predefinedTradingAccount={tradingAccount}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.FROM_TRADING_ACCOUNT_TO_WALLET_TRANSACTION,
            translateKey: (
              <>
                Trading account
                <Tooltip key='right'>
                  <TooltipTrigger>
                    <CircleMinus className='ml-3 size-4 text-red-600' />
                  </TooltipTrigger>
                  <TooltipContent side='right'>
                    'Transfer From Trading Account To Wallet'
                  </TooltipContent>
                </Tooltip>
              </>
            ),
            visible: true,
            permission: PermissionsEnum.CanTransferFromTradingAccountToWalletTransaction,
            component: (
              <TradingAccountToWalletTransferPage
                client={client}
                predefinedTradingAccount={tradingAccount}
                preSelectedWallet={wallet}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.ADJUSTMENTS,
            translateKey: 'Adjustments',
            visible: true,
            permission: PermissionsEnum.CanAddCustomUserWalletTransaction,
            component: (
              <CreateAdjustmentTabs
                client={client}
                preSelectedWallet={wallet}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.MULTI_ADJUSTMENTS,
            translateKey: 'Multi-Adjustments',
            visible: true,
            permission: PermissionsEnum.CanAddCustomUserWalletTransaction,
            component: (
              <MultiAdjustmentPage
                client={client}
                preSelectedWallet={wallet}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.BONUS,
            translateKey: 'Bonus',
            visible: true,
            component: (
              <BonusPage
                client={client}
                preSelectedWallet={wallet}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.PA_DEPOSIT,
            translateKey: 'PA Deposit',
            visible: true,
            permission: PermissionsEnum.CanAddPaymentAgentWalletTransaction,
            component: (
              <PaymentAgentDepositPage
                client={client}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.PA_WITHDRAW,
            translateKey: 'PA Withdrawal',
            visible: true,
            permission: PermissionsEnum.CanAddPaymentAgentWalletTransaction,
            component: (
              <PaymentAgentWithdrawalPage
                client={client}
                setClient={() => setSelectClientModalOpen(true)}
              />
            ),
          },
          {
            key: TransactionTabType.NEGATIVE_EQUITY,
            translateKey: 'Negative Equity',
            visible: true,
            permission: PermissionsEnum.CanAddNegativeEquityTransactions,
            component: <TransactionNegativeEquityPage />,
          },
        ]}
      />
    </Loading>
  )
}

export default TransactionsTab
